import type { StartDateConstraints } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Checkbox, Input, Label, Radio, RadioGroup } from '@fluxora/ui';

/**
 * Start-date picker (E-SF06). The mode comes from the E-SF03 serviceability
 * result carried on the sign context:
 *
 *  - SWITCH  → "first available" (the server's `firstAvailableDate`) OR a chosen
 *              date bounded to the server's [earliest, latest] window.
 *  - MOVE_IN → a chosen move-in date in the same window, plus — only when the
 *              premise has an AMS (smart) meter AND we are before the daily cutoff
 *              (`amsSameDayAvailable`) — a same-day-start checkbox.
 *
 * All bounds are SERVER-COMPUTED (real Texas business-day + AMS-cutoff math); this
 * component only renders them. Controlled: the page owns the state, the service
 * validates and builds the SignatureEvent.
 */
export interface StartDatePickerProps {
  readonly constraints: StartDateConstraints;
  readonly startMode: 'FIRST_AVAILABLE' | 'CHOSEN';
  readonly onStartModeChange: (mode: 'FIRST_AVAILABLE' | 'CHOSEN') => void;
  readonly chosenDate: string;
  readonly onChosenDateChange: (date: string) => void;
  readonly requestSameDay: boolean;
  readonly onRequestSameDayChange: (value: boolean) => void;
  /** i18n key for a start-date error, if any. */
  readonly error?: string;
}

export function StartDatePicker({
  constraints,
  startMode,
  onStartModeChange,
  chosenDate,
  onChosenDateChange,
  requestSameDay,
  onRequestSameDayChange,
  error,
}: StartDatePickerProps): JSX.Element {
  const { t } = useTranslation();
  const isSwitch = constraints.enrollmentType === 'SWITCH';

  const dateField = (
    <div className="flex flex-col gap-1">
      <Label htmlFor="sign-start-date">{t('shop.sign.startDate.date.label')}</Label>
      <Input
        id="sign-start-date"
        name="startDate"
        type="date"
        min={constraints.earliestDate}
        max={constraints.latestDate}
        value={chosenDate}
        onChange={(e) => onChosenDateChange(e.target.value)}
      />
    </div>
  );

  return (
    <section className="flex flex-col gap-3" aria-labelledby="sign-start-heading">
      <h2 id="sign-start-heading" className="text-sm font-medium text-foreground">
        {t('shop.sign.startDate.heading')}
      </h2>
      <p className="text-xs text-foreground/70">
        {isSwitch ? t('shop.sign.startDate.switch.note') : t('shop.sign.startDate.moveIn.note')}
      </p>
      {/* An agent's offer: the date already on the quote is preselected (see
          `resolveStartDateChoice`); this says where it came from. */}
      {constraints.offeredDate !== undefined ? (
        <p className="text-xs text-foreground/70" data-testid="start-date-offered-note">
          {t('shop.sign.startDate.offered.note', { date: constraints.offeredDate })}
        </p>
      ) : null}

      {isSwitch ? (
        <RadioGroup
          name="startMode"
          value={startMode}
          onChange={(v) => onStartModeChange(v === 'CHOSEN' ? 'CHOSEN' : 'FIRST_AVAILABLE')}
          ariaLabel={t('shop.sign.startDate.heading')}
        >
          <Radio
            value="FIRST_AVAILABLE"
            label={t('shop.sign.startDate.firstAvailable.label')}
            description={t('shop.sign.startDate.firstAvailable.desc', {
              date: constraints.firstAvailableDate,
            })}
          />
          <Radio
            value="CHOSEN"
            label={t('shop.sign.startDate.chosen.label')}
            description={t('shop.sign.startDate.chosen.desc')}
          />
          {startMode === 'CHOSEN' ? dateField : null}
        </RadioGroup>
      ) : (
        <div className="flex flex-col gap-3">
          {dateField}
          {constraints.amsSameDayAvailable ? (
            <Checkbox
              name="requestSameDay"
              checked={requestSameDay}
              onChange={(e) => onRequestSameDayChange(e.target.checked)}
              label={t('shop.sign.startDate.sameDay.label')}
              description={t('shop.sign.startDate.sameDay.desc', {
                cutoff: constraints.sameDayCutoffLabel ?? '',
              })}
            />
          ) : null}
        </div>
      )}

      {error !== undefined ? <span className="text-xs text-danger">{t(error)}</span> : null}
    </section>
  );
}
